import type { OrderContactStatus, OrderStatus, PortalOrderRow } from "@khazzin/api-client";
import { ORDER_STATUS_TONES } from "@khazzin/ui/patterns/orderStatus";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { registerStatusTones } from "@khazzin/ui/patterns/statusTone";
import type { Tone } from "@khazzin/ui/styles";
import { useLocale } from "../../app/shell/locale";

/**
 * The order chips the portal draws (EP10-S03), in the Admin Console's colours
 * and — from the shared ui bundle — its words, so a status reads the same to
 * the store as to the warehouse.
 */

registerStatusTones("orders", ORDER_STATUS_TONES);

/** One of the seventeen. */
export function OrderStatusChip({ status }: { status: OrderStatus }) {
	const { t } = useLocale();
	return (
		<StatusChip domain="orders" status={status} size="sm">
			{t(`orders.status.${status}`)}
		</StatusChip>
	);
}

/** The status and the chips that ride beside it: Cashed Out, on hold, Awaiting confirmation. */
export function OrderStatusCell({ row }: { row: PortalOrderRow }) {
	const { t } = useLocale();
	return (
		<span className="flex flex-wrap items-center gap-1">
			<OrderStatusChip status={row.status} />
			{row.secondaryStatus === "cashed_out" && (
				<StatusChip tone="success" size="sm">
					{t("orders.secondaryStatus.cashed_out")}
				</StatusChip>
			)}
			{row.onHold && (
				<StatusChip tone="warning" size="sm">
					{t("portal.orders.chips.onHold")}
				</StatusChip>
			)}
			{row.awaitingConfirmation && (
				<StatusChip tone="warning" size="sm">
					{t("portal.orders.chips.awaitingConfirmation")}
				</StatusChip>
			)}
		</span>
	);
}

const CONTACT_TONES: Record<OrderContactStatus, Tone> = {
	not_contacted: "neutral",
	auto_texted: "info",
	texted: "info",
	called_no_answer: "warning",
	confirmed: "success",
	reschedule_requested: "warning",
	cancel_requested: "danger",
	wrong_number: "danger",
};

/**
 * The Customer Contact chip, read-only (CR-022): where Khazzin's reach of the
 * customer stands. No popover and no action — the store reads it, the
 * warehouse's customer service moves it.
 */
export function ContactChip({ status }: { status: OrderContactStatus }) {
	const { t } = useLocale();
	return (
		<StatusChip tone={CONTACT_TONES[status] ?? "neutral"} size="sm">
			{t(`orders.contactStatus.${status}`)}
		</StatusChip>
	);
}
